<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :title="title"
    :aria-label="ariaLabel || title || 'Action'"
    class="icon-action-btn"
    :class="[
      sizeClass,
      variantClass,
      (disabled || loading) ? 'icon-action-btn--disabled' : ''
    ]"
    @click="onClick"
  >
    <!-- Spinner when loading -->
    <svg
      v-if="loading"
      class="animate-spin"
      :class="spinnerSizeClass"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
      <path
        class="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>

    <!-- Icon slot -->
    <span v-else class="inline-flex items-center justify-center">
      <slot />
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  title?: string
  ariaLabel?: string
  type?: 'button' | 'submit' | 'reset'
  variant?: 'neutral' | 'primary' | 'danger' | 'success' | 'warning'
  size?: 'sm' | 'md' | 'lg'
  shape?: 'icon' | 'pill'
  disabled?: boolean
  loading?: boolean
  ghost?: boolean
  bordered?: boolean
}>()

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void
}>()

const onClick = (e: MouseEvent) => {
  if (props.disabled || props.loading) return
  emit('click', e)
}

const isGhost = computed(() => {
  // Default to ghost mode unless bordered is explicitly true
  return !props.bordered
})

const sizeClass = computed(() => {
  const shape = props.shape ?? 'icon'

  // Ghost mode - padding-based sizing like EmployeeRow.vue (default behavior)
  if (isGhost.value) {
    switch (props.size) {
      case 'sm':
        return 'icon-action-btn--ghost-sm'
      case 'lg':
        return 'icon-action-btn--ghost-lg'
      default:
        return 'icon-action-btn--ghost-md'
    }
  }

  if (shape === 'pill') {
    switch (props.size) {
      case 'sm':
        return 'icon-action-btn--pill-sm'
      case 'lg':
        return 'icon-action-btn--pill-lg'
      default:
        return 'icon-action-btn--pill-md'
    }
  }

  // bordered icon-only (old behavior)
  switch (props.size) {
    case 'sm':
      return 'icon-action-btn--sm'
    case 'lg':
      return 'icon-action-btn--lg'
    default:
      return 'icon-action-btn--md'
  }
})

const spinnerSizeClass = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'w-4 h-4'
    case 'lg':
      return 'w-6 h-6'
    default:
      return 'w-5 h-5'
  }
})

const variantClass = computed(() => {
  // If ghost mode (default), use ghost variant classes
  if (isGhost.value) {
    switch (props.variant) {
      case 'primary':
        return 'icon-action-btn--ghost-primary'
      case 'danger':
        return 'icon-action-btn--ghost-danger'
      case 'success':
        return 'icon-action-btn--ghost-success'
      case 'warning':
        return 'icon-action-btn--ghost-warning'
      default:
        return 'icon-action-btn--ghost-neutral'
    }
  }

  // Bordered mode (old behavior)
  switch (props.variant) {
    case 'primary':
      return 'icon-action-btn--primary'
    case 'danger':
      return 'icon-action-btn--danger'
    case 'success':
      return 'icon-action-btn--success'
    case 'warning':
      return 'icon-action-btn--warning'
    default:
      return 'icon-action-btn--neutral'
  }
})
</script>

<style scoped>
/* Pill sizes (auto width) */
.icon-action-btn--pill-sm { @apply h-8 px-3 gap-1.5 rounded-full; }
.icon-action-btn--pill-md { @apply h-9 px-3.5 gap-2 rounded-full; }
.icon-action-btn--pill-lg { @apply h-10 px-4 gap-2 rounded-full; }


/* Base layout */
.icon-action-btn {
  @apply inline-flex items-center justify-center
         gap-2
         whitespace-nowrap
         rounded-lg
         transition-all duration-200
         focus:outline-none focus:ring-2 focus:ring-offset-2
         focus:ring-primary/40 focus:ring-offset-white dark:focus:ring-offset-slate-900
         shadow-sm hover:shadow-md active:scale-[0.98];
}

/* Sizes (big enough hit area for tables) */
.icon-action-btn--sm { @apply w-8 h-8; }
.icon-action-btn--md { @apply w-9 h-9; }
.icon-action-btn--lg { @apply w-10 h-10; }

/* Ghost sizes - padding-based like EmployeeRow.vue */
.icon-action-btn--ghost-sm { @apply p-2; }
.icon-action-btn--ghost-md { @apply p-2.5; }
.icon-action-btn--ghost-lg { @apply p-3; }

/* Bordered Variants (old behavior - used when bordered=true) */
.icon-action-btn--neutral {
  @apply bg-white/70 dark:bg-slate-800/60
         text-slate-600 dark:text-slate-300
         hover:bg-slate-50 dark:hover:bg-slate-700/60
         hover:text-slate-900 dark:hover:text-white;
}

.icon-action-btn--primary {
  @apply bg-primary/10
         text-primary
         hover:bg-primary/15;
}

.icon-action-btn--danger {
  @apply bg-red-50 dark:bg-red-900/20
         text-red-600 dark:text-red-300
         hover:bg-red-100 dark:hover:bg-red-900/30;
}

.icon-action-btn--success {
  @apply bg-green-50 dark:bg-green-900/20
         text-green-600 dark:text-green-300
         hover:bg-green-100 dark:hover:bg-green-900/30;
}

.icon-action-btn--warning {
  @apply bg-amber-50 dark:bg-amber-900/20
         text-amber-700 dark:text-amber-300
         hover:bg-amber-100 dark:hover:bg-amber-900/30;
}

/* Ghost Variants (default behavior - no background/border, hover-only like EmployeeRow.vue) */
.icon-action-btn--ghost-neutral {
  @apply bg-transparent dark:bg-transparent shadow-none
         text-slate-600/70 dark:text-slate-300/70
         hover:bg-slate-100/50 dark:hover:bg-slate-700/50
         hover:text-slate-900 dark:hover:text-white;
}

.icon-action-btn--ghost-primary {
  @apply bg-transparent dark:bg-transparent shadow-none
         text-primary/70 dark:text-primary-dark/70
         hover:bg-primary/10 dark:hover:bg-primary-dark/10
         hover:text-primary dark:hover:text-primary-dark;
}

.icon-action-btn--ghost-danger {
  @apply bg-transparent dark:bg-transparent shadow-none
         text-danger/70 dark:text-danger-dark/70
         hover:bg-danger/10 dark:hover:bg-danger-dark/10
         hover:text-danger dark:hover:text-danger-dark;
}

.icon-action-btn--ghost-success {
  @apply bg-transparent dark:bg-transparent shadow-none
         text-success/70 dark:text-success-dark/70
         hover:bg-success/10 dark:hover:bg-success-dark/10
         hover:text-success dark:hover:text-success-dark;
}

.icon-action-btn--ghost-warning {
  @apply bg-transparent dark:bg-transparent shadow-none
         text-warning/70 dark:text-warning-dark/70
         hover:bg-warning/10 dark:hover:bg-warning-dark/10
         hover:text-warning dark:hover:text-warning-dark;
}

/* Disabled */
.icon-action-btn--disabled {
  @apply opacity-50 cursor-not-allowed shadow-none hover:shadow-none active:scale-100;
}
</style>
